import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CreditCard,
  ExternalLink,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  ArrowUpRight,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi } from "@/lib/portalApi";

export default function PortalPayments() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const bootstrapped = useRef(false);

  const load = useCallback(async (opts = {}) => {
    try {
      const { data } = await portalApi.get("/portal/stripe/status", {
        params: opts.refresh ? { refresh: "true" } : {},
      });
      setStatus(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load payment status";
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    const back = searchParams.get("onboarding");
    if (back) {
      load({ refresh: true }).then(() => {
        const next = new URLSearchParams(searchParams);
        next.delete("onboarding");
        setSearchParams(next, { replace: true });
        if (back === "return") toast.success("Welcome back! We've refreshed your Stripe status.");
      });
    } else {
      load();
    }
  }, [load, searchParams, setSearchParams]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/stripe/connect", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start Stripe onboarding";
      toast.error(msg, { duration: 8000 });
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/stripe/login-link", {});
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not open Stripe dashboard";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async () => {
    setRefreshing(true);
    await load({ refresh: true });
    toast.success("Status refreshed");
  };

  return (
    <PortalShell>
      <div className="max-w-3xl">
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-2">
              Payments
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F5F5F0]">
              Accept ticket payments via Stripe
            </h1>
            <p className="mt-3 text-[13px] text-[#A3A3A3] max-w-xl leading-relaxed">
              Swingle uses <span className="text-[#F5F5F0]">Stripe Connect (Express)</span>. You&apos;ll
              go through a short Stripe-hosted onboarding to verify your business — once approved,
              buyers pay you directly and funds settle straight to your bank account.
            </p>
          </div>
          <button
            onClick={refreshStatus}
            disabled={refreshing || loading}
            data-testid="stripe-refresh-btn"
            className="hidden sm:inline-flex items-center gap-2 border border-white/10 hover:border-white/25 px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
          >
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="border border-white/10 p-10 flex items-center justify-center text-[#A3A3A3]">
            <Loader2 size={16} className="animate-spin mr-3" /> Loading…
          </div>
        ) : status?.ready ? (
          <ConnectedCard status={status} onOpenDashboard={openDashboard} busy={busy} />
        ) : status?.connected ? (
          <IncompleteCard status={status} onResume={startOnboarding} busy={busy} />
        ) : (
          <NotConnectedCard onStart={startOnboarding} busy={busy} status={status} />
        )}

        <FeeBreakdown status={status} />
      </div>
    </PortalShell>
  );
}

function StatusPill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
        ok
          ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
          : "border-white/10 text-[#A3A3A3]"
      }`}
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-white/25"}`}
      />
      {label}
    </span>
  );
}

function NotConnectedCard({ onStart, busy, status }) {
  return (
    <div
      className="border border-white/10 p-8"
      data-testid="stripe-not-connected-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <CreditCard size={20} className="text-[#D9A6F5]" />
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3]">
          Not connected
        </div>
      </div>
      <h2 className="text-2xl font-black tracking-tight text-[#F5F5F0]">
        Connect Stripe to start selling tickets
      </h2>
      <p className="mt-3 text-[13px] text-[#A3A3A3] leading-relaxed">
        Ticket sales are disabled until Stripe onboarding is complete. It usually takes 3–5 minutes
        — you&apos;ll need your business details, a bank account for payouts, and an ID document.
      </p>

      <ul className="mt-6 space-y-2 text-[12px] text-[#A3A3A3]">
        <li>• Funds settle directly to your bank account.</li>
        <li>• Stripe handles PCI compliance, chargebacks and payouts.</li>
        <li>• You get a Stripe dashboard for refunds, disputes and reporting.</li>
      </ul>

      <button
        onClick={onStart}
        disabled={busy}
        data-testid="stripe-connect-btn"
        className="mt-8 inline-flex items-center gap-2 bg-[#D9A6F5] hover:bg-[#E8C8FF] disabled:opacity-60 text-black font-bold px-5 py-3 text-sm uppercase tracking-widest transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
        {busy ? "Redirecting…" : "Connect with Stripe"}
      </button>

      {status && !status.connected && !busy && (
        <div className="mt-6 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]/60">
          Powered by Stripe Connect Express
        </div>
      )}
    </div>
  );
}

function IncompleteCard({ status, onResume, busy }) {
  return (
    <div
      className="border border-amber-500/30 bg-amber-500/5 p-8"
      data-testid="stripe-incomplete-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle size={20} className="text-amber-300" />
        <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300">
          Onboarding incomplete
        </div>
      </div>
      <h2 className="text-2xl font-black tracking-tight text-[#F5F5F0]">
        Finish your Stripe onboarding
      </h2>
      <p className="mt-3 text-[13px] text-[#A3A3A3] leading-relaxed">
        Your Stripe account is created but Stripe still needs some info before you can start
        accepting payments. This is usually a bank account, an ID document, or a business
        verification.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        <StatusPill ok={status?.details_submitted} label="Details submitted" />
        <StatusPill ok={status?.charges_enabled} label="Charges enabled" />
        <StatusPill ok={status?.payouts_enabled} label="Payouts enabled" />
      </div>

      <button
        onClick={onResume}
        disabled={busy}
        data-testid="stripe-resume-btn"
        className="mt-8 inline-flex items-center gap-2 bg-[#D9A6F5] hover:bg-[#E8C8FF] disabled:opacity-60 text-black font-bold px-5 py-3 text-sm uppercase tracking-widest transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
        {busy ? "Redirecting…" : "Resume onboarding"}
      </button>
    </div>
  );
}

function ConnectedCard({ status, onOpenDashboard, busy }) {
  return (
    <div
      className="border border-emerald-500/30 bg-emerald-500/[0.03] p-8"
      data-testid="stripe-connected-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck size={20} className="text-emerald-300" />
        <div className="text-[11px] uppercase tracking-[0.28em] text-emerald-300">
          Ready to accept payments
        </div>
      </div>
      <h2 className="text-2xl font-black tracking-tight text-[#F5F5F0]">
        You&apos;re all set
      </h2>
      <p className="mt-3 text-[13px] text-[#A3A3A3] leading-relaxed">
        Stripe verification is complete. Charges are enabled and payouts are turned on. Buyers
        will pay you directly at checkout — you can manage refunds and payouts from your Stripe
        dashboard.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        <StatusPill ok label="Details submitted" />
        <StatusPill ok label="Charges enabled" />
        <StatusPill ok={status?.payouts_enabled} label="Payouts enabled" />
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={onOpenDashboard}
          disabled={busy}
          data-testid="stripe-dashboard-btn"
          className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D9A6F5]/40 hover:text-[#D9A6F5] text-[#F5F5F0] px-5 py-3 text-sm uppercase tracking-widest transition-colors"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
          Open Stripe dashboard
        </button>
      </div>

      {status?.account_id && (
        <div
          className="mt-6 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]/60"
          data-testid="stripe-account-id"
        >
          Account: {status.account_id}
        </div>
      )}
    </div>
  );
}

function FeeBreakdown({ status }) {
  const bookingFee = ((status?.booking_fee_pence ?? 195) / 100).toFixed(2);
  return (
    <div className="mt-10 border border-white/10 p-6">
      <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-4">
        How the fee model works
      </div>
      <ul className="space-y-3 text-[13px] text-[#F5F5F0]">
        <li className="flex gap-3">
          <span className="text-[#D9A6F5]">•</span>
          <span>
            A <span className="text-[#D9A6F5] font-bold">£{bookingFee} booking fee</span> is added
            to the buyer&apos;s total for every ticket.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="text-[#D9A6F5]">•</span>
          <span>
            <span className="text-[#D9A6F5] font-bold">You keep 100% of your ticket price</span>{" "}
            plus a flat <span className="text-[#D9A6F5] font-bold">£0.20 bonus per ticket</span> —
            Swingle covers the Stripe processing fee out of our share of the booking fee.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="text-[#D9A6F5]">•</span>
          <span>
            On <span className="text-[#D9A6F5] font-bold">AMEX / EEA / non-UK cards</span> Stripe&apos;s
            fee is higher. Swingle automatically refunds the extra back to your balance shortly
            after each charge — the 20p bonus still lands with you.
          </span>
        </li>
      </ul>

      {/* Worked examples */}
      <div
        className="mt-6 border-t border-white/10 pt-6 space-y-8"
        data-testid="fee-worked-example"
      >
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3]">
          Worked examples — 1 × £40 ticket
        </div>

        <WorkedExample
          testId="fee-example-uk"
          label="UK debit / credit card"
          stripeFeeRate="1.5% + 20p"
          stripeFee="0.83"
          platformFee="0.92"
          refund={null}
        />

        <WorkedExample
          testId="fee-example-eea"
          label="EEA card (e.g. France, Germany, Spain)"
          stripeFeeRate="2.5% + 20p"
          stripeFee="1.25"
          platformFee="0.92"
          refund="0.42"
        />

        <WorkedExample
          testId="fee-example-amex"
          label="AMEX / non-UK international card"
          stripeFeeRate="3.25% + 20p"
          stripeFee="1.56"
          platformFee="0.92"
          refund="0.73"
        />

        <p className="text-[11px] text-[#A3A3A3]/80 leading-relaxed">
          Regardless of the card the buyer uses, you always receive £40.20 — that&apos;s 100%
          of your ticket price plus our £0.20 loyalty bonus. Swingle&apos;s share shrinks on
          foreign / AMEX cards because we absorb the extra Stripe cost automatically via a
          refund back to your balance.
        </p>
      </div>
    </div>
  );
}

function WorkedExample({ testId, label, stripeFeeRate, stripeFee, platformFee, refund }) {
  const swingleNet = refund
    ? (parseFloat(platformFee) - parseFloat(refund)).toFixed(2)
    : platformFee;
  return (
    <div data-testid={testId} className="border border-white/10 p-5">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
        {label}
      </div>
      <dl className="space-y-2 text-[13px]">
        <ExampleRow label="Ticket price you set" value="£40.00" />
        <ExampleRow label="Booking fee added at checkout" value="+ £1.95" />
        <ExampleRow label="Buyer pays at checkout" value="£41.95" emphasis />
        <ExampleRow
          label={`Stripe processing fee (${stripeFeeRate})`}
          value={`− £${stripeFee}`}
          muted
        />
        <ExampleRow
          label="Swingle application fee"
          value={`− £${platformFee}`}
          muted
        />
        {refund && (
          <ExampleRow
            label="Swingle refund back to your balance"
            value={`+ £${refund}`}
            muted
          />
        )}
        <ExampleRow label="You receive (£40 + £0.20 bonus)" value="£40.20" highlight />
      </dl>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
        Swingle&apos;s share on this transaction: £{swingleNet}
      </div>
    </div>
  );
}

function ExampleRow({ label, value, emphasis, muted, highlight }) {
  const rowCls = highlight
    ? "border-t border-white/10 pt-2 mt-1"
    : "";
  const labelCls = muted ? "text-[#A3A3A3]" : "text-[#F5F5F0]";
  const valueCls = highlight
    ? "text-[#D9A6F5] font-black text-base tracking-tight"
    : emphasis
    ? "text-[#F5F5F0] font-bold"
    : muted
    ? "text-[#A3A3A3]"
    : "text-[#F5F5F0]";
  return (
    <div className={`flex items-baseline justify-between gap-6 ${rowCls}`}>
      <dt className={labelCls}>{label}</dt>
      <dd className={`tabular-nums ${valueCls}`}>{value}</dd>
    </div>
  );
}
